import React, { useCallback, useMemo } from "react";
import classNames from "classnames";
import { useField } from "formik";
import { RuleGroupType } from "@common/types/transactionRule";
import { KICK_GROUP_ICON } from "@common/constants/rules";
import { Picker } from "@design-system/Picker/Picker.lib";
import { useWorkspaceRuleGroupsList } from "@api/rules.api";
import { FolderIcon } from "@icons";
import styles from "../RuleForm.module.scss";
import { TransactionRuleFormSchema } from "../constants";

const GROUP_TYPE_LABELS: Record<RuleGroupType, string> = {
    [RuleGroupType.TRANSACTION]: "Transaction",
    [RuleGroupType.ACCOUNTING]: "Accounting",
    [RuleGroupType.SPLIT]: "Split",
    [RuleGroupType.TRANSFER]: "Transfer",
};

const GROUP_TYPE_ORDER: RuleGroupType[] = [
    RuleGroupType.TRANSACTION,
    RuleGroupType.ACCOUNTING,
    RuleGroupType.SPLIT,
    RuleGroupType.TRANSFER,
];

interface RuleGroupFormProps {
    groupType: RuleGroupType;
    onTypeChange?: (type: RuleGroupType) => void;
}

export const RuleGroupForm: React.FC<RuleGroupFormProps> = ({
    groupType,
    onTypeChange,
}) => {
    const ruleGroupsList = useWorkspaceRuleGroupsList();
    const [{ value: groupId }, , { setValue: setGroupId }] =
        useField<TransactionRuleFormSchema["groupId"]>("groupId");

    const allGroups = useMemo(
        () =>
            ruleGroupsList.data?.filter(
                (group) => group.icon !== KICK_GROUP_ICON,
            ) ?? [],
        [ruleGroupsList.data],
    );

    const availableTypeItems = useMemo(
        () =>
            GROUP_TYPE_ORDER.filter((type) =>
                allGroups.some((g) => g.type === type),
            ).map((type) => ({
                value: type,
                label: GROUP_TYPE_LABELS[type],
            })),
        [allGroups],
    );

    const filteredGroups = useMemo(
        () => allGroups.filter((g) => g.type === groupType),
        [allGroups, groupType],
    );

    const groupItems = useMemo(
        () =>
            filteredGroups.map((group) => ({
                value: group.id,
                label: group.name,
            })),
        [filteredGroups],
    );

    const pickerGroupId =
        filteredGroups.find((group) => group.id === groupId)?.id ?? null;

    const onGroupChange = useCallback(
        (selectedGroupId: string) => {
            setGroupId(selectedGroupId);
        },
        [setGroupId],
    );

    const onTypeChangeInternal = useCallback(
        (value: RuleGroupType | null) => {
            if (value && onTypeChange) {
                const newTypeGroups = allGroups.filter((g) => g.type === value);
                const defaultGroup =
                    newTypeGroups.find((g) => g.isDefault) ?? newTypeGroups[0];
                if (defaultGroup) {
                    setGroupId(defaultGroup.id);
                }
                onTypeChange(value);
            }
        },
        [allGroups, onTypeChange, setGroupId],
    );

    return (
        <section className={styles.sectionGroup}>
            <header className={styles.header}>
                <div className={styles.group}>
                    <aside
                        className={classNames(
                            styles.sectionIcon,
                            styles.sectionIconGroup,
                        )}
                    >
                        <FolderIcon className="icon-color-gray-600" />
                    </aside>
                    <span className={styles.sectionTitle}>Rule details</span>
                </div>
            </header>

            <main className={styles.content}>
                <div className={styles.ruleDetailsRow}>
                    {onTypeChange && (
                        <>
                            <span className={styles.ruleDetailsLabel}>
                                Type
                            </span>
                            <Picker.Select
                                items={availableTypeItems}
                                value={groupType}
                                onValueChange={onTypeChangeInternal}
                                renderValue={() => GROUP_TYPE_LABELS[groupType]}
                                placeholder="Select"
                                disabled
                            />
                        </>
                    )}

                    <span className={styles.ruleDetailsLabel}>Group</span>
                    <Picker.Select
                        items={groupItems}
                        value={pickerGroupId}
                        onValueChange={(value) => {
                            if (value !== null) {
                                onGroupChange(value);
                            }
                        }}
                        placeholder="Select"
                    />
                </div>
            </main>
        </section>
    );
};
